import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Pie, PieChart } from 'recharts'

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { DashboardSectionHeader } from './DashboardSectionHeader'
import type { PointsLedgerRecord } from '@/types/points'

function getMonthOptions(t: (key: string) => string) {
  return [
    { value: 'all', label: t('dashboard.charts.allYear') },
    { value: '0', label: t('dashboard.months.january') },
    { value: '1', label: t('dashboard.months.february') },
    { value: '2', label: t('dashboard.months.march') },
    { value: '3', label: t('dashboard.months.april') },
    { value: '4', label: t('dashboard.months.may_long') },
    { value: '5', label: t('dashboard.months.june') },
    { value: '6', label: t('dashboard.months.july') },
    { value: '7', label: t('dashboard.months.august') },
    { value: '8', label: t('dashboard.months.september') },
    { value: '9', label: t('dashboard.months.october') },
    { value: '10', label: t('dashboard.months.november') },
    { value: '11', label: t('dashboard.months.december') },
  ] as const
}

function getChartConfig(t: (key: string) => string) {
  return {
    available: {
      label: t('dashboard.charts.availablePoints'),
      color: 'var(--chart-1)',
    },
    used: {
      label: t('dashboard.charts.usedPoints'),
      color: 'var(--chart-4)',
    },
  } satisfies ChartConfig
}

function getLedgerDate(entry: PointsLedgerRecord) {
  const raw = entry.effective_at ?? entry.created_at
  if (!raw) return null
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? null : date
}

function collectYears(entries: PointsLedgerRecord[]) {
  const years = new Set<number>()
  const currentYear = new Date().getFullYear()

  for (const entry of entries) {
    const date = getLedgerDate(entry)
    if (date) years.add(date.getFullYear())
  }

  years.add(currentYear)
  return Array.from(years).sort((a, b) => b - a)
}

function buildPointsBreakdown(entries: PointsLedgerRecord[], year: number, month: string, labels: { available: string; used: string }) {
  const filtered = entries.filter((entry) => {
    const date = getLedgerDate(entry)
    if (!date || date.getFullYear() !== year) return false
    if (month === 'all') return true
    return date.getMonth() === Number(month)
  })

  const available = filtered.reduce((sum, entry) => {
    if (entry.entry_status !== 'available' || entry.points_delta <= 0) return sum
    return sum + entry.points_delta
  }, 0)

  const used = filtered.reduce((sum, entry) => {
    const isConsumed = entry.entry_type === 'spend' || entry.entry_status === 'consumed'
    if (!isConsumed || entry.points_delta >= 0) return sum
    return sum + Math.abs(entry.points_delta)
  }, 0)

  return [
    {
      segment: 'available',
      label: labels.available,
      value: available,
      fill: 'var(--color-available)',
    },
    {
      segment: 'used',
      label: labels.used,
      value: used,
      fill: 'var(--color-used)',
    },
  ]
}

export interface PointsBalancePieChartProps {
  ledgerEntries: PointsLedgerRecord[]
}

export function PointsBalancePieChart({ ledgerEntries }: PointsBalancePieChartProps) {
  const { t } = useTranslation()
  const years = React.useMemo(() => collectYears(ledgerEntries), [ledgerEntries])

  const defaultYear = String(new Date().getFullYear())
  const [year, setYear] = React.useState(defaultYear)
  const [month, setMonth] = React.useState<string>('all')

  React.useEffect(() => {
    if (years.includes(Number(year))) return
    setYear(String(years[0] ?? Number(defaultYear)))
  }, [years, year, defaultYear])

  const yearNumber = Number(year)
  const chartConfig = React.useMemo(() => getChartConfig(t), [t])
  const monthOptions = React.useMemo(() => getMonthOptions(t), [t])
  const chartData = React.useMemo(
    () => buildPointsBreakdown(ledgerEntries, yearNumber, month, { available: chartConfig.available.label, used: chartConfig.used.label }),
    [ledgerEntries, yearNumber, month, chartConfig],
  )

  const totalPoints = chartData.reduce((sum, item) => sum + item.value, 0)

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={month} onValueChange={setMonth}>
        <SelectTrigger size="sm" className="w-[9.5rem]">
          <SelectValue placeholder={t('dashboard.charts.monthPlaceholder')} />
        </SelectTrigger>
        <SelectContent align="end">
          <SelectGroup>
            <SelectLabel>{t('dashboard.charts.monthPlaceholder')}</SelectLabel>
            {monthOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>

      <Select value={year} onValueChange={setYear}>
        <SelectTrigger size="sm" className="w-[7.5rem]">
          <SelectValue placeholder={t('dashboard.charts.yearPlaceholder')} />
        </SelectTrigger>
        <SelectContent align="end">
          <SelectGroup>
            <SelectLabel>{t('dashboard.charts.yearPlaceholder')}</SelectLabel>
            {years.map((value) => (
              <SelectItem key={value} value={String(value)}>
                {value}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )

  return (
    <>
      <DashboardSectionHeader
        title={t('dashboard.charts.pointsDistributionTitle')}
        description={t('dashboard.charts.pointsDistributionDescription')}
        actions={actions}
      />

      {totalPoints === 0 ? (
        <div className="flex min-h-[280px] items-center justify-center rounded-lg border border-dashed border-border/70 bg-muted/10 text-sm text-muted-foreground">
          {t('dashboard.charts.pointsNoData')}
        </div>
      ) : (
        <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[280px]">
          <PieChart>
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  hideLabel
                  nameKey="segment"
                  formatter={(value, name) => (
                    <div className="flex w-full items-center justify-between gap-3">
                      <span className="text-muted-foreground">
                        {chartConfig[name as keyof typeof chartConfig]?.label ?? name}
                      </span>
                      <span className="font-medium text-foreground">
                        {Number(value).toLocaleString(t('language') === 'fr' ? 'fr-FR' : 'en-US')} {t('common.pts')}
                      </span>
                    </div>
                  )}
                />
              }
            />
            <ChartLegend content={<ChartLegendContent nameKey="segment" />} />
            <Pie data={chartData} dataKey="value" nameKey="segment" strokeWidth={0} />
          </PieChart>
        </ChartContainer>
      )}
    </>
  )
}

export function PointsBalancePieChartSkeleton() {
  return (
    <>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <div className="min-w-0 space-y-1.5">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-64 max-w-[90%]" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Skeleton className="h-8 w-[9.5rem]" />
          <Skeleton className="h-8 w-[7.5rem]" />
        </div>
      </div>

      <div className="mx-auto flex aspect-square max-h-[280px] w-full items-center justify-center rounded-lg bg-muted/10">
        <Skeleton className="size-44 rounded-full" />
      </div>
    </>
  )
}
